import React, { useEffect, useState } from 'react';
import type { ReplacementLogEntry } from '../../../services/phoneNumberHealthService';
import PhoneNumbersSection from './PhoneNumbersSection';

interface Props {
  entries: ReplacementLogEntry[];
  suggestions: Array<{
    id: string;
    old_phone_number_id: string;
    suggested_number_e164: string | null;
    area_code: string | null;
    reason: string | null;
    status: string;
  }>;
  onApprove: (suggestionId: string) => void;
  onReject: (suggestionId: string) => void;
}

const ReplacementLog: React.FC<Props> = ({ entries, suggestions, onApprove, onReject }) => (
  <div className="space-y-6">
    {suggestions.length > 0 && (
      <PhoneNumbersSection title="Pending Replacements" description="Review suggested swaps before they go live in campaigns.">
        <ul className="space-y-3">
          {suggestions.map((s) => (
            <li
              key={s.id}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm rounded-xl border border-amber-200/80 dark:border-amber-800/50 bg-amber-50/40 dark:bg-amber-950/10 p-4"
            >
              <div>
                <p className="font-medium">
                  Area <span className="font-mono">{s.area_code ?? '—'}</span>
                  {s.suggested_number_e164 ? (
                    <span className="text-muted-foreground"> → <span className="font-mono">{s.suggested_number_e164}</span></span>
                  ) : (
                    <span className="text-red-600 font-normal"> — No inventory</span>
                  )}
                </p>
                {s.reason && <p className="text-xs text-muted-foreground mt-1">{s.reason}</p>}
              </div>
              {s.status === 'pending' && s.suggested_number_e164 && (
                <div className="flex gap-2 shrink-0">
                  <button type="button" className="text-xs px-3 py-2 rounded-lg border hover:bg-muted font-medium" onClick={() => onReject(s.id)}>
                    Reject
                  </button>
                  <button type="button" className="text-xs px-3 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:opacity-90" onClick={() => onApprove(s.id)}>
                    Approve
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </PhoneNumbersSection>
    )}

    <PhoneNumbersSection title="Replacement History" description="Completed number swaps and quarantine log." noPadding>
      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-12">No replacements yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Old Number</th>
                <th className="px-4 py-3 font-medium">New Number</th>
                <th className="px-4 py-3 font-medium">Area</th>
                <th className="px-4 py-3 font-medium">Reason</th>
                <th className="px-4 py-3 font-medium">By</th>
                <th className="px-4 py-3 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-t border-border/60 hover:bg-muted/20">
                  <td className="px-4 py-3 font-mono text-xs">{e.old_number}</td>
                  <td className="px-4 py-3 font-mono text-xs">{e.new_number}</td>
                  <td className="px-4 py-3">{e.area_code}</td>
                  <td className="px-4 py-3 text-xs max-w-[180px] truncate" title={e.reason_replaced ?? ''}>{e.reason_replaced}</td>
                  <td className="px-4 py-3 capitalize">{e.replaced_by}</td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">{new Date(e.replacement_date).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PhoneNumbersSection>
  </div>
);

export default ReplacementLog;
